// src/pages/pengamatan/buah-alami/detail-tabs/DataObservasiTab.jsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Chip
} from '@mui/material';
import DataObservasiTableHeader from './DataObservasiTableHeader';

const DataObservasiTab = ({ pengamatanData, filteredPlots, sourceTab }) => {
    const getPlotStatusColor = (status) => {
        switch (status) {
            case 'Approved':
                return 'success';
            case 'Processed':
                return 'info';
            case 'Pending':
                return 'warning';
            case 'Draft':
                return 'default';
            default:
                return 'error';
        }
    };

    // Fungsi untuk menghilangkan angka di belakang titik
    const removeDecimal = (value) => {
        if (value === '-' || value === '' || value === null || value === undefined) return '-';
        
        const stringValue = value.toString();
        if (stringValue.includes('.')) {
            return stringValue.split('.')[0];
        }
        return stringValue;
    };

    // Fungsi untuk format angka dengan separator ribuan
    const formatRibuan = (value) => {
        if (value === '-' || value === '' || value === null || value === undefined || isNaN(value)) return '-';
        
        const num = parseFloat(value);
        if (num === 0) return '-';
        
        return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Fungsi untuk menghitung populasi
    const calculatePopulation = (value, pjp) => {
        if (value === '-' || value === '' || value === null || value === undefined || isNaN(value)) return '-';
        
        const numValue = parseFloat(value);
        const numPjp = parseFloat(pjp) || 1;
        
        if (numValue === 0 || numPjp === 0) return '-';
        
        const result = (numValue / numPjp) * 16666.67;
        return formatRibuan(result);
    };

    // Fungsi untuk mengambil data dari struktur Firebase yang sesuai
    const getFaseBungaData = (plotData) => {
        const faseBunga = plotData.data_entry?.fase_bunga || {};
        return {
            open_heart: faseBunga.open_heart_fb_ba || '-',
            red_bud_125: faseBunga.red_bud_125_fb_ba || '-',
            red_bud_254: faseBunga.red_bud_254_fb_ba || '-',
            early_cones: faseBunga.early_cones_fb_ba || '-',
            mid_cones: faseBunga.mid_cones_fb_ba || '-',
            late_cones: faseBunga.late_cones_fb_ba || '-',
            early_petals: faseBunga.early_petals_fb_ba || '-',
            mid_petals: faseBunga.mid_petals_fb_ba || '-',
            late_petals: faseBunga.late_petals_fb_ba || '-',
            dry_petals: faseBunga.dry_petals_fb_ba || '-',
            filling_stage_1: faseBunga.filling_stage_1_fb_ba || '-',
            filling_stage_2: faseBunga.filling_stage_2_fb_ba || '-',
            sc0: faseBunga.sc0_fb_ba || '-',
            sc0_lebih: faseBunga.sc0_ld_fb_ba || '-',
            terpetik: faseBunga.terpetik_fb_ba || '-',
            dimakan_tikus: faseBunga.dimakan_tikus_fb_ba || '-'
        };
    };

    // Fungsi untuk mengambil data tanaman dari struktur Firebase
    const getTanamanData = (plotData) => {
        const tanaman = plotData.data_entry?.tanaman || {};
        const penyakit = tanaman.penyakit_tanaman || {};
        return {
            normal: tanaman.normal_t_ba || '-',
            penyakit_phy: penyakit.phy_pt_ba || '-',
            penyakit_erw: penyakit.erw_pt_ba || '-',
            kerdil: tanaman.kerdil_t_ba || '-'
        };
    };

    // Fungsi untuk mengambil data buah alami dari struktur Firebase - MODIFIKASI
    const getBuahAlamiData = (plotData) => {
        const dataEntry = plotData.data_entry || {};
        return {
            buah_alami: removeDecimal(dataEntry.buah_alami_ba) || '-', // HILANGKAN DESIMAL
            mati: dataEntry.mati_ba || '-',
            total: removeDecimal(dataEntry.total_ba) || '-', // HILANGKAN DESIMAL
            spot_mati: dataEntry.spot_mati_ba || '-'
        };
    };

    // Fungsi untuk mengambil data tanaman pinggiran dari struktur Firebase - MODIFIKASI
    const getTanamanPinggiranData = (plotData) => {
        const tanamanPinggiran = plotData.data_entry?.tanaman_pinggiran || {};
        const penyakit = tanamanPinggiran.penyakit_tanaman_pinggiran || {};
        return {
            normal: tanamanPinggiran.normal_tp_ba || '-',
            penyakit_phy: penyakit.phy_ptp_ba || '-',
            penyakit_erw: penyakit.erw_ptp_ba || '-',
            kerdil: tanamanPinggiran.kerdil_tp_ba || '-',
            buah_alami_pinggiran: tanamanPinggiran.buah_alami_tp_ba || '-',
            dimakan_tikus_pinggiran: tanamanPinggiran.dimakan_tikus_tp_ba || '-'
        };
    };

    // Fungsi untuk mengambil data buah alami pinggiran dari struktur Firebase - MODIFIKASI
    const getBuahAlamiPinggiranData = (plotData) => {
        const tanamanPinggiran = plotData.data_entry?.tanaman_pinggiran || {};
        return {
            mati_pinggiran: tanamanPinggiran.mati_tp_ba || '-',
            total_pinggiran: removeDecimal(tanamanPinggiran.total_tp_ba) || '-' // HILANGKAN DESIMAL
        };
    };

    const plots = filteredPlots && filteredPlots.length > 0 
        ? filteredPlots 
        : (() => {
            if (!pengamatanData?.plot) return [];
            return Object.entries(pengamatanData.plot)
                .map(([plotKey, plotData]) => ({
                    plotKey,
                    ...plotData
                }))
                .sort((a, b) => {
                    const plotA = a.plot || '';
                    const plotB = b.plot || '';
                    if (!isNaN(plotA) && !isNaN(plotB)) {
                        return parseInt(plotA) - parseInt(plotB);
                    }
                    return plotA.localeCompare(plotB);
                });
        })();

    // FUNGSI BARU: Menghitung total luas plot
    const calculateTotalLuas = () => {
        let total = 0;
        plots.forEach(plot => {
            const luas = parseFloat(plot.luas_plot) || 0;
            total += luas;
        });
        return total;
    };

    // FUNGSI BARU: Menghitung total PJP
    const calculateTotalPJP = () => {
        let total = 0;
        plots.forEach(plot => {
            const pjp = parseFloat(plot.pjp) || 0;
            total += pjp;
        });
        return total;
    };

    // FUNGSI BARU: Menghitung total untuk fase bunga
    const calculateTotalFaseBunga = () => {
        const totals = {
            open_heart: 0,
            red_bud_125: 0,
            red_bud_254: 0,
            early_cones: 0,
            mid_cones: 0,
            late_cones: 0,
            early_petals: 0,
            mid_petals: 0,
            late_petals: 0,
            dry_petals: 0,
            filling_stage_1: 0,
            filling_stage_2: 0,
            sc0: 0,
            sc0_lebih: 0,
            terpetik: 0,
            dimakan_tikus: 0
        };

        plots.forEach(plot => {
            const faseBungaData = getFaseBungaData(plot);
            
            Object.keys(totals).forEach(key => {
                const value = parseFloat(faseBungaData[key]) || 0;
                totals[key] += value;
            });
        });

        return totals;
    };

    // FUNGSI BARU: Menghitung total untuk tanaman (kecuali normal)
    const calculateTotalTanaman = () => {
        const totals = {
            normal: 0,
            penyakit_phy: 0,
            penyakit_erw: 0,
            kerdil: 0
        };

        plots.forEach(plot => {
            const tanamanData = getTanamanData(plot);
            
            totals.normal += parseFloat(tanamanData.normal) || 0;
            totals.penyakit_phy += parseFloat(tanamanData.penyakit_phy) || 0;
            totals.penyakit_erw += parseFloat(tanamanData.penyakit_erw) || 0;
            totals.kerdil += parseFloat(tanamanData.kerdil) || 0;
        });

        return totals;
    };

    // FUNGSI BARU: Menghitung total untuk buah alami
    const calculateTotalBuahAlami = () => {
        const totals = {
            buah_alami: 0,
            mati: 0,
            total: 0,
            spot_mati: 0
        };

        plots.forEach(plot => {
            const buahAlamiData = getBuahAlamiData(plot);
            
            totals.buah_alami += parseFloat(buahAlamiData.buah_alami) || 0;
            totals.mati += parseFloat(buahAlamiData.mati) || 0;
            totals.total += parseFloat(buahAlamiData.total) || 0;
            totals.spot_mati += parseFloat(buahAlamiData.spot_mati) || 0;
        });

        return totals;
    };

    // FUNGSI BARU: Menghitung total untuk tanaman pinggiran
    const calculateTotalTanamanPinggiran = () => {
        const totals = {
            normal: 0,
            penyakit_phy: 0,
            penyakit_erw: 0,
            kerdil: 0,
            buah_alami_pinggiran: 0,
            dimakan_tikus_pinggiran: 0
        };

        plots.forEach(plot => {
            const tanamanPinggiranData = getTanamanPinggiranData(plot);
            
            totals.normal += parseFloat(tanamanPinggiranData.normal) || 0;
            totals.penyakit_phy += parseFloat(tanamanPinggiranData.penyakit_phy) || 0;
            totals.penyakit_erw += parseFloat(tanamanPinggiranData.penyakit_erw) || 0;
            totals.kerdil += parseFloat(tanamanPinggiranData.kerdil) || 0;
            totals.buah_alami_pinggiran += parseFloat(tanamanPinggiranData.buah_alami_pinggiran) || 0;
            totals.dimakan_tikus_pinggiran += parseFloat(tanamanPinggiranData.dimakan_tikus_pinggiran) || 0;
        });

        return totals;
    };

    // FUNGSI BARU: Menghitung total untuk buah alami pinggiran
    const calculateTotalBuahAlamiPinggiran = () => {
        const totals = {
            mati_pinggiran: 0,
            total_pinggiran: 0
        };

        plots.forEach(plot => {
            const buahAlamiPinggiranData = getBuahAlamiPinggiranData(plot);
            
            totals.mati_pinggiran += parseFloat(buahAlamiPinggiranData.mati_pinggiran) || 0;
            totals.total_pinggiran += parseFloat(buahAlamiPinggiranData.total_pinggiran) || 0;
        });

        return totals;
    };

    // FUNGSI BARU: Menghitung total Normal (Populasi)
    const calculateTotalNormalPopulasi = () => {
        const totalNormalTanaman = calculateTotalTanaman().normal;
        const totalPJP = calculateTotalPJP();
        
        if (totalNormalTanaman > 0 && totalPJP > 0) {
            const result = (totalNormalTanaman / totalPJP) * 16666.67;
            return formatRibuan(result);
        }
        return '-';
    };

    // FUNGSI BARU: Menghitung total Total (Populasi)
    const calculateTotalTotalPopulasi = () => {
        const totalBuahAlami = calculateTotalBuahAlami().total;
        const totalPJP = calculateTotalPJP();
        
        if (totalBuahAlami > 0 && totalPJP > 0) {
            const result = (totalBuahAlami / totalPJP) * 16666.67;
            return formatRibuan(result);
        }
        return '-';
    };

    const totalLuas = calculateTotalLuas();
    const totalPJP = calculateTotalPJP();
    const totalFaseBunga = calculateTotalFaseBunga();
    const totalTanaman = calculateTotalTanaman();
    const totalBuahAlami = calculateTotalBuahAlami();
    const totalTanamanPinggiran = calculateTotalTanamanPinggiran();
    const totalBuahAlamiPinggiran = calculateTotalBuahAlamiPinggiran();
    const totalNormalPopulasi = calculateTotalNormalPopulasi();
    const totalTotalPopulasi = calculateTotalTotalPopulasi();

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Data Observasi
            </Typography>
            
            <Card>
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1800 }}>
                        <DataObservasiTableHeader />
                        <TableBody>
                            {plots.map((plot) => {
                                // Ambil data dengan format yang sesuai
                                const faseBungaData = getFaseBungaData(plot);
                                const tanamanData = getTanamanData(plot);
                                const buahAlamiData = getBuahAlamiData(plot);
                                const tanamanPinggiranData = getTanamanPinggiranData(plot);
                                const buahAlamiPinggiranData = getBuahAlamiPinggiranData(plot);

                                // Hitung populasi normal dan total
                                const populasiNormal = calculatePopulation(tanamanData.normal, plot.pjp);
                                const populasiTotal = calculatePopulation(buahAlamiData.total, plot.pjp);

                                return (
                                    <TableRow key={plot.plotKey}>
                                        {/* Plot */}
                                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                            {plot.plot || '-'}
                                        </TableCell>
                                        
                                        {/* Luas */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {plot.luas_plot || '-'}
                                        </TableCell>
                                        
                                        {/* PJP */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {plot.pjp || '-'}
                                        </TableCell>

                                        {/* Populasi - Normal */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {populasiNormal}
                                        </TableCell>

                                        {/* Populasi - Total */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {populasiTotal}
                                        </TableCell>
                                        
                                        {/* Fase Bunga - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.open_heart}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.red_bud_125}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.red_bud_254}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.early_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.mid_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.late_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.early_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.mid_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.late_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.dry_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.filling_stage_1}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.filling_stage_2}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.sc0}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.sc0_lebih}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.terpetik}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {faseBungaData.dimakan_tikus}
                                        </TableCell>
                                        
                                        {/* Tanaman - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanData.normal}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanData.penyakit_phy}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanData.penyakit_erw}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanData.kerdil}
                                        </TableCell>

                                        {/* Buah Alami - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {buahAlamiData.buah_alami}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {buahAlamiData.mati}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {buahAlamiData.total}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {buahAlamiData.spot_mati}
                                        </TableCell>

                                        {/* Tanaman Pinggiran - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanPinggiranData.normal}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanPinggiranData.penyakit_phy}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanPinggiranData.penyakit_erw}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanPinggiranData.kerdil}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {tanamanPinggiranData.buah_alami_pinggiran}
                                        </TableCell>

                                        {/* Buah Alami Pinggiran - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {buahAlamiPinggiranData.mati_pinggiran}
                                        </TableCell>
                                        <TableCell>
                                            {buahAlamiPinggiranData.total_pinggiran}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* BARIS TOTAL BARU */}
                            {plots.length > 0 && (
                                <TableRow sx={{ backgroundColor: '#f5f5f5', '& td': { fontWeight: 'bold', borderTop: '2px solid #e0e0e0' } }}>
                                    {/* Plot - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        Total
                                    </TableCell>
                                    
                                    {/* Total Luas */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalLuas > 0 ? totalLuas.toFixed(2) : '-'}
                                    </TableCell>
                                    
                                    {/* Total PJP */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalPJP > 0 ? totalPJP.toFixed(0) : '-'}
                                    </TableCell>

                                    {/* Total Normal (Populasi) */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalNormalPopulasi}
                                    </TableCell>

                                    {/* Total Total (Populasi) */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTotalPopulasi}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Total */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.open_heart > 0 ? removeDecimal(totalFaseBunga.open_heart.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.red_bud_125 > 0 ? removeDecimal(totalFaseBunga.red_bud_125.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.red_bud_254 > 0 ? removeDecimal(totalFaseBunga.red_bud_254.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.early_cones > 0 ? removeDecimal(totalFaseBunga.early_cones.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.mid_cones > 0 ? removeDecimal(totalFaseBunga.mid_cones.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.late_cones > 0 ? removeDecimal(totalFaseBunga.late_cones.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.early_petals > 0 ? removeDecimal(totalFaseBunga.early_petals.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.mid_petals > 0 ? removeDecimal(totalFaseBunga.mid_petals.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.late_petals > 0 ? removeDecimal(totalFaseBunga.late_petals.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.dry_petals > 0 ? removeDecimal(totalFaseBunga.dry_petals.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.filling_stage_1 > 0 ? removeDecimal(totalFaseBunga.filling_stage_1.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.filling_stage_2 > 0 ? removeDecimal(totalFaseBunga.filling_stage_2.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.sc0 > 0 ? removeDecimal(totalFaseBunga.sc0.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.sc0_lebih > 0 ? removeDecimal(totalFaseBunga.sc0_lebih.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.terpetik > 0 ? removeDecimal(totalFaseBunga.terpetik.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalFaseBunga.dimakan_tikus > 0 ? removeDecimal(totalFaseBunga.dimakan_tikus.toString()) : '-'}
                                    </TableCell>
                                    
                                    {/* Tanaman - Total */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanaman.normal > 0 ? removeDecimal(totalTanaman.normal.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanaman.penyakit_phy > 0 ? removeDecimal(totalTanaman.penyakit_phy.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanaman.penyakit_erw > 0 ? removeDecimal(totalTanaman.penyakit_erw.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanaman.kerdil > 0 ? removeDecimal(totalTanaman.kerdil.toString()) : '-'}
                                    </TableCell>

                                    {/* Buah Alami - Total */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalBuahAlami.buah_alami > 0 ? removeDecimal(totalBuahAlami.buah_alami.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalBuahAlami.mati > 0 ? removeDecimal(totalBuahAlami.mati.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalBuahAlami.total > 0 ? removeDecimal(totalBuahAlami.total.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalBuahAlami.spot_mati > 0 ? removeDecimal(totalBuahAlami.spot_mati.toString()) : '-'}
                                    </TableCell>

                                    {/* Tanaman Pinggiran - Total */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanamanPinggiran.normal > 0 ? removeDecimal(totalTanamanPinggiran.normal.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanamanPinggiran.penyakit_phy > 0 ? removeDecimal(totalTanamanPinggiran.penyakit_phy.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanamanPinggiran.penyakit_erw > 0 ? removeDecimal(totalTanamanPinggiran.penyakit_erw.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanamanPinggiran.kerdil > 0 ? removeDecimal(totalTanamanPinggiran.kerdil.toString()) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalTanamanPinggiran.buah_alami_pinggiran > 0 ? removeDecimal(totalTanamanPinggiran.buah_alami_pinggiran.toString()) : '-'}
                                    </TableCell>

                                    {/* Buah Alami Pinggiran - Total */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {totalBuahAlamiPinggiran.mati_pinggiran > 0 ? removeDecimal(totalBuahAlamiPinggiran.mati_pinggiran.toString()) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {totalBuahAlamiPinggiran.total_pinggiran > 0 ? removeDecimal(totalBuahAlamiPinggiran.total_pinggiran.toString()) : '-'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {plots.length === 0 && (
                    <Typography color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                        Tidak ada data plot
                    </Typography>
                )}
            </Card>
        </Box>
    );
};

export default DataObservasiTab;